import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Form, Input, Modal, notification, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    ModalForm,
    ProFormDatePicker,
    ProFormDigit,
    ProFormSelect,
    ProFormSwitch,
    ProFormText,
    ProFormTextArea
} from '@ant-design/pro-components';
import axiosApiInstance from '../../../api/axiosClient';
import { useCampaignQuery, useCampaignRoster, useCreateCampaign, useUpdateCampaign } from './queryHooks';
import DeliverableManager from './components/DeliverableManager';
import type { Brand, Campaign, CampaignCreator, CampaignDeliverable } from './types';

type CreatorOption = {
    label: string;
    value: number;
    creator: CampaignCreator;
};

/**
 * CampaignEditModal — create or edit a campaign.
 *
 * - URL `/creators/campaigns/new` → blank form, POST on save
 * - URL `/creators/campaigns/:id` → loads via React Query, PUT on save
 * - Creators field is a debounced async multi-select bound to the form's
 *   `creators` field. Like ContactLinkField, it bridges the form's id-array
 *   binding to the backend's [{id, ...}] shape via getValueProps + normalize.
 */
const CampaignEditModal: React.FC = () => {
    const navigate = useNavigate();
    const { id: rawId } = useParams<{ id: string }>();
    const isNew = !rawId || rawId === 'new';
    const id = isNew ? undefined : Number(rawId);

    const { data: shell } = useCampaignQuery(id);
    const { data: roster } = useCampaignRoster(id);
    const existing = useMemo(
        () => (shell ? { ...shell, ...(roster ?? {}) } : undefined),
        [shell, roster]
    );
    const create = useCreateCampaign();
    const update = useUpdateCampaign();

    const [form] = Form.useForm<Campaign>();

    // --- Deliverable templates (campaign-level) ---
    const [deliverableTemplates, setDeliverableTemplates] = useState<CampaignDeliverable[]>([]);
    const [hashtags, setHashtags] = useState<string[]>([]);

    // --- Brand state ---
    const [brands, setBrands] = useState<Brand[]>([]);
    const [brandSearch, setBrandSearch] = useState('');
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [newBrandModalOpen, setNewBrandModalOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');
    const [creatingBrand, setCreatingBrand] = useState(false);

    // Fetch brands (debounced)
    useEffect(() => {
        const handle = setTimeout(() => {
            setLoadingBrands(true);
            axiosApiInstance
                .get<Brand[]>('/api/campaigns/brands', {
                    params: { search: brandSearch || undefined }
                })
                .then(({ data }) => setBrands(data ?? []))
                .catch((err) => console.error('brand search failed', err))
                .finally(() => setLoadingBrands(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [brandSearch]);

    const handleCreateBrand = async () => {
        if (!newBrandName.trim()) return;
        setCreatingBrand(true);
        try {
            const { data: created } = await axiosApiInstance.post<Brand>(
                '/api/campaigns/brands',
                { name: newBrandName.trim() }
            );
            notification.success({ message: `Brand "${created.name}" created` });
            setBrands((prev) => [created, ...prev]);
            form.setFieldValue('brand_id', created.id);
            setNewBrandName('');
            setNewBrandModalOpen(false);
        } catch (err: any) {
            notification.error({
                message: 'Failed to create brand',
                description: err?.response?.data?.error ?? err?.message,
            });
        } finally {
            setCreatingBrand(false);
        }
    };

    const brandOptions = brands.map((b) => ({ label: b.name, value: b.id }));

    // --- Creator search state ---
    const [search, setSearch] = useState<string>('');
    const [options, setOptions] = useState<CreatorOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [creatorsById, setCreatorsById] = useState<Record<number, CampaignCreator>>({});

    useEffect(() => {
        if (!existing) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.setFieldsValue({
            ...existing,
            start_date: existing.start_date ? dayjs(existing.start_date) : null,
            end_date: existing.end_date ? dayjs(existing.end_date) : null,
            paid_media_start: existing.paid_media_start ? dayjs(existing.paid_media_start) : null,
            paid_media_end: existing.paid_media_end ? dayjs(existing.paid_media_end) : null,
            competition_start: existing.competition_start ? dayjs(existing.competition_start) : null,
            competition_end: existing.competition_end ? dayjs(existing.competition_end) : null,
        } as any);
        if (existing.creators?.length) {
            setCreatorsById((prev) => {
                const next = { ...prev };
                for (const c of existing.creators ?? []) next[c.id] = c;
                return next;
            });
        }
        // Ensure the existing brand appears in the list
        if (existing.brand) {
            setBrands((prev) => {
                if (prev.some((b) => b.id === existing.brand!.id)) return prev;
                return [existing.brand!, ...prev];
            });
        }
        if (existing.deliverables2?.length) {
            setDeliverableTemplates(existing.deliverables2);
        }
        if (existing.hashtags?.length) {
            setHashtags(existing.hashtags.map((h) => h.tag));
        }
    }, [existing, form]);

    // Debounced creator search. The creators index endpoint returns
    // { data: Creator[], total: number } and filters via `search`. We must
    // pass the full follower-range defaults explicitly because the backend
    // treats an unset `*_followers_max` as 0 and filters everyone out.
    useEffect(() => {
        const handle = setTimeout(() => {
            setLoading(true);
            axiosApiInstance
                .get<API.DataResponse<CampaignCreator[]>>('/api/creators/items', {
                    params: {
                        search,
                        page: 1,
                        page_size: 50,
                        youtube_followers_min: 0,
                        youtube_followers_max: 1000000,
                        instagram_followers_min: 0,
                        instagram_followers_max: 1000000,
                        tiktok_followers_min: 0,
                        tiktok_followers_max: 1000000,
                    }
                })
                .then(({ data }) => {
                    const list = (data?.data ?? []).slice(0, 50);
                    setOptions(
                        list.map((c) => ({
                            label: c.creator || `Creator #${c.id}`,
                            value: c.id,
                            creator: c
                        }))
                    );
                    setCreatorsById((prev) => {
                        const next = { ...prev };
                        for (const c of list) next[c.id] = c;
                        return next;
                    });
                })
                .catch((err) => console.error('creator search failed', err))
                .finally(() => setLoading(false));
        }, 300);
        return () => clearTimeout(handle);
    }, [search]);

    const allOptions = useMemo<CreatorOption[]>(() => {
        const seen = new Set(options.map((o) => o.value));
        const extra: CreatorOption[] = [];
        for (const [k, v] of Object.entries(creatorsById)) {
            const idNum = Number(k);
            if (!seen.has(idNum)) {
                extra.push({ label: v.creator || `Creator #${idNum}`, value: idNum, creator: v });
            }
        }
        return [...options, ...extra];
    }, [options, creatorsById]);

    return (
        <>
        <ModalForm<Campaign>
            open
            form={form}
            title={isNew ? 'New Campaign' : 'Edit Campaign'}
            width={720}
            modalProps={{
                destroyOnClose: true,
                onCancel: () =>
                    navigate(isNew ? '/creators/campaigns' : `/creators/campaigns/${id}/creators`)
            }}
            initialValues={existing ?? { status: 'spec' }}
            onFinish={async (values) => {
                const payload: Campaign = {
                    ...values,
                    start_date: values.start_date ? dayjs(values.start_date).toISOString() : null,
                    end_date: values.end_date ? dayjs(values.end_date).toISOString() : null,
                    paid_media_start: values.paid_media_start ? dayjs(values.paid_media_start).toISOString() : null,
                    paid_media_end: values.paid_media_end ? dayjs(values.paid_media_end).toISOString() : null,
                    competition_start: values.competition_start ? dayjs(values.competition_start).toISOString() : null,
                    competition_end: values.competition_end ? dayjs(values.competition_end).toISOString() : null,
                    deliverables2: deliverableTemplates,
                    hashtags: hashtags.map((tag) => ({ tag })),
                };
                try {
                    if (isNew) {
                        await create.mutateAsync(payload);
                        notification.success({ message: 'Campaign created' });
                        navigate('/creators/campaigns');
                    } else {
                        await update.mutateAsync({ id: id!, ...payload });
                        notification.success({ message: 'Campaign saved' });
                        navigate(`/creators/campaigns/${id}/creators`);
                    }
                    return true;
                } catch (err) {
                    console.error(err);
                    notification.error({ message: 'Save failed' });
                    return false;
                }
            }}
        >
            <ProFormText
                name="name"
                label="Campaign Name"
                placeholder="e.g. Lidl Easter"
                rules={[{ required: true, message: 'Name is required' }]}
            />
            <Form.Item
                name="brand_id"
                label="Brand"
                rules={[{ required: true, message: 'Brand is required' }]}
            >
                <Select
                    showSearch
                    allowClear
                    placeholder="Select a brand…"
                    filterOption={false}
                    loading={loadingBrands}
                    onSearch={setBrandSearch}
                    options={brandOptions}
                    notFoundContent={loadingBrands ? 'Searching…' : 'No brands found'}
                    style={{ width: '100%' }}
                    dropdownRender={(menu) => (
                        <>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ padding: '0 8px 4px' }}>
                                <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    style={{ paddingLeft: 0 }}
                                    onClick={() => setNewBrandModalOpen(true)}
                                >
                                    Create new brand
                                </Button>
                            </div>
                        </>
                    )}
                />
            </Form.Item>
            <ProFormDigit
                name="budget"
                label="Budget (€)"
                min={0}
                fieldProps={{ precision: 2, style: { width: '100%' } }}
            />
            <ProFormTextArea
                name="brief"
                label="Brief"
                fieldProps={{ rows: 4 }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <ProFormDatePicker
                    name="start_date"
                    label="Start Date"
                    fieldProps={{ style: { width: '100%' } }}
                />
                <ProFormDatePicker
                    name="end_date"
                    label="End Date"
                    fieldProps={{ style: { width: '100%' } }}
                />
            </div>
            <ProFormSelect
                name="status"
                label="Status"
                options={[
                    { label: 'Spec', value: 'spec' },
                    { label: 'Active', value: 'active' },
                    { label: 'Completed', value: 'completed' },
                    { label: 'Canceled', value: 'canceled' }
                ]}
            />

            <Form.Item label="Keywords" extra="Caps and accents are folded, so ΗΒΗ, Ήβη, and ήβη are the same keyword. On Refresh, posts are kept when the caption, title, or description contains any keyword. Posts with no text go to Drafts.">
                <Select
                    mode="tags"
                    tokenSeparators={[',']}
                    placeholder="e.g. lidl, HBH"
                    value={hashtags}
                    onChange={(v) => setHashtags(v.map((t) => String(t).replace(/^#/, '')))}
                    style={{ width: '100%' }}
                />
            </Form.Item>

            <Form.Item
                name="creators"
                label="Creators"
                getValueProps={(value: CampaignCreator[] | undefined) => ({
                    value: (value ?? []).map((c) => c.id)
                })}
                normalize={(ids: number[]) =>
                    (ids ?? []).map((id) => ({ id })) as unknown as CampaignCreator[]
                }
            >
                <Select<number[]>
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder="Search and add creators by name…"
                    filterOption={false}
                    loading={loading}
                    onSearch={setSearch}
                    options={allOptions}
                    notFoundContent={loading ? 'Searching…' : 'No matches'}
                    style={{ width: '100%' }}
                />
            </Form.Item>

            <Form.Item label="Default deliverables" extra="New creators on this campaign start with these. Open one to edit it.">
                <DeliverableManager
                    items={deliverableTemplates}
                    onCreate={(d) => setDeliverableTemplates((prev) => [...prev, d])}
                    onUpdate={(index, d) => setDeliverableTemplates((prev) => prev.map((item, i) => (i === index ? { ...item, ...d } : item)))}
                    onDelete={(index) => setDeliverableTemplates((prev) => prev.filter((_, i) => i !== index))}
                />
            </Form.Item>

            <Divider orientation="left" orientationMargin={0} style={{ fontSize: 13, color: '#888' }}>Paid Media</Divider>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: '0 16px', alignItems: 'end' }}>
                <ProFormSwitch
                    name="paid_media"
                    label="Paid Media"
                />
                <ProFormDatePicker
                    name="paid_media_start"
                    label="Paid Media Start"
                    fieldProps={{ style: { width: '100%' } }}
                />
                <ProFormDatePicker
                    name="paid_media_end"
                    label="Paid Media End"
                    fieldProps={{ style: { width: '100%' } }}
                />
            </div>

            <Divider orientation="left" orientationMargin={0} style={{ fontSize: 13, color: '#888' }}>Competition</Divider>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <ProFormDatePicker
                    name="competition_start"
                    label="Competition Start"
                    fieldProps={{ style: { width: '100%' } }}
                />
                <ProFormDatePicker
                    name="competition_end"
                    label="Competition End"
                    fieldProps={{ style: { width: '100%' } }}
                />
            </div>
            <ProFormTextArea
                name="competition_comments"
                label="Competition Comments"
                fieldProps={{ rows: 2 }}
            />
        </ModalForm>

        <Modal
            title="New Brand"
            open={newBrandModalOpen}
            okText="Create"
            cancelText="Cancel"
            okButtonProps={{ disabled: !newBrandName.trim(), loading: creatingBrand }}
            onOk={handleCreateBrand}
            onCancel={() => { setNewBrandModalOpen(false); setNewBrandName(''); }}
            destroyOnClose
        >
            <Form layout="vertical" style={{ marginTop: 8 }}>
                <Form.Item label="Brand Name" required>
                    <Input
                        autoFocus
                        placeholder="e.g. Lidl"
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        onPressEnter={handleCreateBrand}
                    />
                </Form.Item>
            </Form>
        </Modal>
        </>
    );
};

export default CampaignEditModal;
